import PublicLayout from "@/components/layout/PublicLayout";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchArticles } from "@/lib/api";
import { BookOpen, Clock, Tag, FileSearch, Scale, Wrench, ArrowDown } from "lucide-react";

export default function Learn() {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState("All");

    useEffect(() => {
        fetchArticles()
            .then(setArticles)
            .finally(() => setLoading(false));
    }, []);

    const categories = useMemo(() => {
        const cats = Array.from(new Set(articles.map((a) => a.category))).filter(Boolean);
        cats.sort();
        return ["All", ...cats];
    }, [articles]);

    const categoryCounts = useMemo(() => {
        const counts = { All: articles.length };
        for (const a of articles) {
            if (!a.category) continue;
            counts[a.category] = (counts[a.category] || 0) + 1;
        }
        return counts;
    }, [articles]);

    const visibleArticles = useMemo(() => {
        if (activeCategory === "All") return articles;
        return articles.filter((a) => a.category === activeCategory);
    }, [articles, activeCategory]);

    const featured = visibleArticles.filter((a) => a.featured);
    const rest = visibleArticles.filter((a) => !a.featured);

    const avgReading = useMemo(() => {
        const rt = articles
            .map((a) => Number(a.reading_time) || 0)
            .filter((n) => n > 0);
        if (!rt.length) return 0;
        return Math.round(rt.reduce((a, b) => a + b, 0) / rt.length);
    }, [articles]);

    // SEO: emit CollectionPage + ItemList JSON-LD so Google can treat the
    // /learn index as an editorial library (eligible for sitelinks + richer
    // SERP cards). Same injection pattern as LearnArticle.jsx.
    useEffect(() => {
        if (!articles.length) return;
        const origin = (typeof window !== "undefined" && window.location?.origin) || "";
        const pageUrl = `${origin}/learn`;

        const itemList = {
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Buyer's Leverage editorial library",
            itemListOrder: "https://schema.org/ItemListOrderDescending",
            numberOfItems: articles.length,
            itemListElement: articles.map((a, idx) => ({
                "@type": "ListItem",
                position: idx + 1,
                url: `${origin}/learn/${a.slug}`,
                item: {
                    "@type": "LearningResource",
                    name: a.title,
                    url: `${origin}/learn/${a.slug}`,
                    description: a.excerpt || undefined,
                    learningResourceType: "Article",
                    educationalLevel: "beginner",
                    inLanguage: "en-US",
                    about: a.category || undefined,
                    timeRequired: a.reading_time ? `PT${a.reading_time}M` : undefined,
                    isAccessibleForFree: true,
                },
            })),
        };

        const collectionPage = {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Editorial guidance for informed buyers",
            description:
                "Calm, useful articles on inspection findings, negotiation framing, repair costs, and closing preparation.",
            url: pageUrl,
            isPartOf: { "@type": "WebSite", name: "Buyer's Leverage", url: origin },
            mainEntity: itemList,
        };

        const inserted = [];
        try {
            [["collectionpage", collectionPage], ["itemlist", itemList]].forEach(([key, blob]) => {
                const el = document.createElement("script");
                el.type = "application/ld+json";
                el.setAttribute("data-bl-schema", `learn-${key}`);
                el.text = JSON.stringify(blob);
                document.head.appendChild(el);
                inserted.push(el);
            });
        } catch {
            /* no-op */
        }
        return () => inserted.forEach((el) => el.remove());
    }, [articles]);

    return (
        <PublicLayout>
            {/* Hero — split layout: copy left, category panel right */}
            <section className="bl-paper">
                <div className="bl-container pt-16 md:pt-20 pb-12" data-testid="learn-page">
                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                        <div className="lg:col-span-7" data-reveal="slide-left-lg">
                            <div className="bl-eyebrow">Learn</div>
                            <h1 className="mt-5 font-display font-semibold text-[1.75rem] sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.1] sm:leading-[1.05] lg:leading-[1.02] text-bl max-w-3xl">
                                Editorial guidance for{" "}
                                <span className="font-italic-accent text-bl-primary">
                                    informed buyers.
                                </span>
                            </h1>
                            <p className="mt-6 text-bl-muted text-lg max-w-2xl leading-relaxed">
                                Calm, useful articles on inspection findings, negotiation
                                framing, repair costs, and closing preparation.
                            </p>

                            {/* What you'll find inside — fills empty space below headline */}
                            <div className="mt-10 grid sm:grid-cols-3 gap-4 max-w-2xl" data-testid="learn-hero-pillars">
                                <div className="rounded-xl border border-bl bg-white/60 px-4 py-4">
                                    <FileSearch className="w-4 h-4 text-bl-primary" />
                                    <div className="mt-3 font-display font-semibold text-bl leading-tight">
                                        Inspection findings
                                    </div>
                                    <p className="mt-1 text-xs text-bl-muted leading-relaxed">
                                        What inspectors flag, what it usually means, what to ignore.
                                    </p>
                                </div>
                                <div className="rounded-xl border border-bl bg-white/60 px-4 py-4">
                                    <Scale className="w-4 h-4 text-bl-primary" />
                                    <div className="mt-3 font-display font-semibold text-bl leading-tight">
                                        Negotiation framing
                                    </div>
                                    <p className="mt-1 text-xs text-bl-muted leading-relaxed">
                                        How to ask for credits without losing the deal.
                                    </p>
                                </div>
                                <div className="rounded-xl border border-bl bg-white/60 px-4 py-4">
                                    <Wrench className="w-4 h-4 text-bl-primary" />
                                    <div className="mt-3 font-display font-semibold text-bl leading-tight">
                                        Real repair costs
                                    </div>
                                    <p className="mt-1 text-xs text-bl-muted leading-relaxed">
                                        Ballpark ranges drawn from actual contractor quotes.
                                    </p>
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={() => {
                                    const grid = document.getElementById("learn-grid");
                                    if (grid) grid.scrollIntoView({ behavior: "smooth", block: "start" });
                                }}
                                data-testid="learn-hero-scroll-cta"
                                className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-bl-primary hover:text-bl transition-colors group"
                            >
                                Browse the library
                                <ArrowDown className="w-3.5 h-3.5 transition-transform group-hover:translate-y-0.5" />
                            </button>
                        </div>

                        {/* Right-side: at-a-glance + category browser */}
                        <div className="lg:col-span-5 min-w-0" data-reveal="slide-right-lg" data-reveal-delay="2">
                            <div className="bl-card p-6 md:p-7" data-testid="learn-hero-side">
                                <div className="flex items-center justify-between">
                                    <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                        <BookOpen className="w-3.5 h-3.5" />
                                        Editorial library
                                    </div>
                                    <div className="font-mono-bl text-xs text-bl-muted">
                                        {articles.length} articles · {categories.length - 1} categories
                                    </div>
                                </div>

                                <div className="mt-5 grid grid-cols-2 gap-3">
                                    <div className="rounded-xl border border-bl px-4 py-3">
                                        <div className="text-[10px] tracking-[0.16em] uppercase text-bl-muted">
                                            Avg read
                                        </div>
                                        <div className="mt-1 flex items-baseline gap-1">
                                            <span className="font-display text-2xl font-semibold text-bl">
                                                {avgReading || "—"}
                                            </span>
                                            <span className="text-xs text-bl-muted">min</span>
                                        </div>
                                    </div>
                                    <div className="rounded-xl border border-bl px-4 py-3">
                                        <div className="text-[10px] tracking-[0.16em] uppercase text-bl-muted">
                                            Updated
                                        </div>
                                        <div className="mt-1 font-display text-2xl font-semibold text-bl">
                                            Weekly
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-6">
                                    <div className="text-[10px] tracking-[0.18em] uppercase text-bl-muted flex items-center gap-1.5">
                                        <Tag className="w-3 h-3" /> Browse by category
                                    </div>
                                    <ul className="mt-3 space-y-1">
                                        {categories.slice(0, 8).map((c) => (
                                            <li key={"side-" + c}>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setActiveCategory(c);
                                                        const grid = document.getElementById("learn-grid");
                                                        if (grid) {
                                                            grid.scrollIntoView({ behavior: "smooth", block: "start" });
                                                        }
                                                    }}
                                                    data-testid={`learn-side-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                                                    className={
                                                        "w-full flex items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors " +
                                                        (activeCategory === c
                                                            ? "bg-bl-primary/10 text-bl font-medium"
                                                            : "text-bl-muted hover:bg-bl-cream hover:text-bl")
                                                    }
                                                >
                                                    <span>{c}</span>
                                                    <span className="font-mono-bl text-[11px] text-bl-muted">
                                                        {categoryCounts[c] || 0}
                                                    </span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Editorial collage — claymation inspection findings */}
            <section className="bl-paper" data-testid="learn-collage-section">
                <div className="bl-container pb-12 md:pb-16">
                    <div className="max-w-5xl mx-auto" data-reveal="rise-sm">
                        <h2
                            className="font-display font-semibold text-bl text-center text-2xl sm:text-3xl md:text-4xl leading-tight tracking-tight"
                            data-testid="learn-collage-heading"
                        >
                            Understand what matters.{" "}
                            <span className="font-italic-accent text-bl-primary">
                                Ignore what doesn&apos;t.
                            </span>
                        </h2>
                        <div className="mt-7 md:mt-9 overflow-hidden rounded-3xl border border-bl shadow-sm bg-bl-cream">
                            <picture>
                                <source srcSet="/learn-hero-collage.webp" type="image/webp" />
                                <img
                                    src="/learn-hero-collage.png"
                                    alt="Claymation collage of common home inspection findings: foundation crack, electrical panel, HVAC unit, roof, and water intrusion."
                                    loading="lazy"
                                    className="w-full h-auto block"
                                    data-testid="learn-collage-image"
                                />
                            </picture>
                        </div>

                        {/* Vignette cards — each opens the relevant category */}
                        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="learn-collage-cards">
                            {[
                                { label: "Foundation crack", cat: "Foundation", note: "Differential settling, water entry paths." },
                                { label: "Electrical panel", cat: "Electrical", note: "Double-tapped breakers, aluminum branch wiring." },
                                { label: "HVAC unit", cat: "HVAC", note: "Age, refrigerant type, service-life math." },
                                { label: "Roof", cat: "Roofing", note: "Shingle wear, flashing, remaining life." },
                                { label: "Water intrusion", cat: "Plumbing", note: "Active vs. historical, source mapping." },
                            ].map((v) => (
                                <button
                                    key={v.label}
                                    type="button"
                                    onClick={() => {
                                        setActiveCategory(v.cat);
                                        const grid = document.getElementById("learn-grid");
                                        if (grid) grid.scrollIntoView({ behavior: "smooth", block: "start" });
                                    }}
                                    data-testid={`learn-vignette-${v.cat.toLowerCase()}`}
                                    className="text-left rounded-xl border border-bl bg-white px-4 py-3 transition-all hover:border-bl-primary hover:-translate-y-0.5 hover:shadow-sm"
                                >
                                    <div className="text-[10px] tracking-[0.16em] uppercase text-bl-muted">
                                        What we look for
                                    </div>
                                    <div className="mt-1 font-display font-semibold text-sm text-bl leading-tight">
                                        {v.label}
                                    </div>
                                    <p className="mt-1 text-[11px] text-bl-muted leading-snug">
                                        {v.note}
                                    </p>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Category filter chips bar */}
            <section className="border-y border-bl bg-bl-surface sticky top-0 z-30" id="learn-grid">
                <div className="bl-container py-4 overflow-x-auto">
                    <div className="flex items-center gap-2 min-w-max">
                        <span className="text-[11px] tracking-[0.18em] uppercase text-bl-muted mr-2 shrink-0">
                            Filter
                        </span>
                        {categories.map((c) => {
                            const active = activeCategory === c;
                            return (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => setActiveCategory(c)}
                                    data-testid={`learn-cat-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={
                                        "px-3.5 py-1.5 rounded-full text-sm font-medium transition-all border " +
                                        (active
                                            ? "bg-bl-primary text-white border-bl-primary shadow-sm"
                                            : "bg-white text-bl-muted border-bl hover:border-bl-primary hover:text-bl")
                                    }
                                >
                                    {c}
                                    <span
                                        className={
                                            "ml-2 font-mono-bl text-[10px] " +
                                            (active ? "text-white/85" : "text-bl-muted")
                                        }
                                    >
                                        {categoryCounts[c] || 0}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </section>

            {loading ? (
                <div className="bl-container py-16 text-center text-bl-muted">
                    <div className="bl-spinner mx-auto mb-4" /> Loading…
                </div>
            ) : (
                <section className="bl-container py-12 space-y-16" data-reveal>
                    {featured.length > 0 && (
                        <div data-testid="learn-featured-section">
                            <div className="bl-eyebrow">Featured</div>
                            <div className="mt-6 grid md:grid-cols-2 gap-6">
                                {featured.map((a, i) => (
                                    <Link
                                        key={a.slug}
                                        to={`/learn/${a.slug}`}
                                        className="bl-card bl-card-hover block overflow-hidden"
                                        data-testid={`learn-card-${a.slug}`}
                                        data-reveal="rise-sm"
                                        data-reveal-delay={Math.min(5, i + 1)}
                                    >
                                        {a.hero_image?.url && (
                                            <div className="aspect-[16/9] bg-bl-cream overflow-hidden">
                                                <img
                                                    src={`${process.env.REACT_APP_BACKEND_URL}${a.hero_image.url}?w=900`}
                                                    alt={a.featured_image_alt || a.title}
                                                    loading="lazy"
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                        )}
                                        <div className="p-7 md:p-9">
                                            <div className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                                {a.category}
                                            </div>
                                            <h2 className="mt-3 font-display font-semibold text-2xl md:text-3xl text-bl leading-tight">
                                                {a.title}
                                            </h2>
                                            <p className="mt-3 text-bl-muted leading-relaxed">
                                                {a.excerpt}
                                            </p>
                                            <div className="mt-5 font-mono-bl text-xs text-bl-muted flex items-center gap-1">
                                                <Clock className="w-3 h-3" /> {a.reading_time} min read
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    <div>
                        <div className="flex items-baseline justify-between flex-wrap gap-3">
                            <div className="bl-eyebrow">
                                {activeCategory === "All"
                                    ? "Browse all"
                                    : `Category · ${activeCategory}`}
                            </div>
                            <div className="font-mono-bl text-xs text-bl-muted">
                                {visibleArticles.length}{" "}
                                {visibleArticles.length === 1 ? "article" : "articles"}
                            </div>
                        </div>

                        {visibleArticles.length === 0 ? (
                            <div className="mt-8 bl-card p-8 text-center text-bl-muted">
                                <p>No articles in this category yet.</p>
                                <button
                                    type="button"
                                    onClick={() => setActiveCategory("All")}
                                    className="mt-3 text-bl-primary text-sm font-medium underline"
                                    data-testid="learn-reset-filter"
                                >
                                    Browse all articles instead
                                </button>
                            </div>
                        ) : (
                            <div className="mt-6 grid md:grid-cols-3 gap-5">
                                {rest.concat(featured).map((a, i) => (
                                    <Link
                                        key={"all-" + a.slug}
                                        to={`/learn/${a.slug}`}
                                        className="bl-card bl-card-hover block overflow-hidden"
                                        data-testid={`learn-list-${a.slug}`}
                                        data-reveal="rise-sm"
                                        data-reveal-delay={Math.min(5, (i % 3) + 1)}
                                    >
                                        {a.hero_image?.url && (
                                            <div className="aspect-[16/9] bg-bl-cream overflow-hidden">
                                                <img
                                                    src={`${process.env.REACT_APP_BACKEND_URL}${a.hero_image.url}?w=600`}
                                                    alt={a.featured_image_alt || a.title}
                                                    loading="lazy"
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                        )}
                                        <div className="p-6">
                                            <div className="text-[10px] tracking-[0.18em] uppercase text-bl-muted">
                                                {a.category}
                                            </div>
                                            <h3 className="mt-2 font-display font-semibold text-lg text-bl">
                                                {a.title}
                                            </h3>
                                            <p className="mt-2 text-sm text-bl-muted leading-relaxed">
                                                {a.excerpt}
                                            </p>
                                            <div className="mt-4 font-mono-bl text-[10px] text-bl-muted flex items-center gap-1">
                                                <Clock className="w-3 h-3" /> {a.reading_time} min
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            )}
        </PublicLayout>
    );
}
